import { useState } from 'react';
import styled from '@emotion/styled';
import {
  Button,
  breakpoints,
  fontStyle,
  Modal,
  spacing,
  palette,
  Form,
} from '@playdapp/ui';
import { useMedia } from 'react-use';

import { useAppSelector } from 'store/hooks';
import { getBreakpointQuery, convertAddress } from 'lib/util';
import { SimplexPurchaseResponseData } from 'types/simplex';

type Props = {
  digitalAmount: number | string;
  digitalCurrency: number | string;
  fiatAmount: number | string;
  fiatCurrency: number | string;
  isOpen: boolean;
  confirmData: SimplexPurchaseResponseData;
  handleOpen: (isOpen: boolean) => void;
};

const ModalContents = styled.div`
  @media (min-height: 480px) and (max-width: 480px) {
    max-height: 80vh;
    overflow-y: scroll;
  }

  @media (max-height: 480px) {
    max-height: 60vh;
    overflow-y: scroll;
  }
`;

const TitleBlock = styled.div`
  text-align: center;
`;

const Title = styled.span`
  ${fontStyle('h4')};

  ${breakpoints.down('sm')} {
    ${fontStyle('h5')};
  }
`;

const SubTitleBlock = styled.div`
  margin-top: ${spacing.xs};
  margin-bottom: ${spacing.m};
  text-align: center;
`;

const SubTitle = styled.span`
  ${fontStyle('p3')};
  color: ${palette.gray900};
`;

const ContentsBlock = styled.div`
  width: 100%;
  padding: ${spacing.l};
  background-color: ${palette.gray200};
  border-radius: 16px;
`;

const ContentsTop = styled.div`
  padding-bottom: ${spacing.m};
  border-bottom: 1px solid #d9d9d9;

  ${breakpoints.down('sm')} {
    padding-bottom: ${spacing.s};
  }
`;

const ContentsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;

  & + & {
    margin-top: ${spacing.s};
  }
`;

const ContentsName = styled.span`
  ${fontStyle('p4')};
  color: ${palette.gray900};
`;

const ContentsValue = styled.span`
  ${fontStyle('b3')};
  color: ${palette.black};
`;

const ContentsBottom = styled.div`
  padding-top: ${spacing.m};
  text-align: center;

  p:nth-of-type(1) {
    ${fontStyle('p5')};
    color: ${palette.gray900};
  }

  p:nth-of-type(2) {
    margin-top: ${spacing.xs};
    ${fontStyle('p4')};
    color: ${palette.black};
  }

  ${breakpoints.down('sm')} {
    padding-top: ${spacing.s};
  }
`;

const DisclaimerBlock = styled.div`
  margin-top: ${spacing.xl};
  padding-bottom: ${spacing.m};
  border-bottom: 1px solid #d9d9d9;
`;

const DisclaimerTitleBox = styled.div`
  margin-bottom: ${spacing.xs};

  span {
    ${fontStyle('b4')};
    color: ${palette.black};
  }
`;

const DisclaimerContents = styled.p`
  ${fontStyle('p5')};
  color: ${palette.gray900};

  span {
    color: ${palette.primary600};
  }
`;

const CheckBoxBlock = styled.div`
  margin-top: ${spacing.m};

  form > div {
    margin-bottom: 0;

    label {
      padding-left: 4px;
      line-height: 2.1 !important;
      ${fontStyle('p4')};
      color: ${palette.black};
    }
  }
`;

const ButtonBlock = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  margin-top: ${spacing.xl};

  button {
    width: 50%;
    height: auto;
    min-height: 2.5rem;
  }

  ${breakpoints.down('sm')} {
    align-items: space-between;
    justify-content: center;

    button {
      width: 100%;
    }
  }
`;

const SimplexConfirmModal = ({
  digitalAmount,
  digitalCurrency,
  fiatAmount,
  fiatCurrency,
  isOpen,
  handleOpen,
  confirmData,
}: Props) => {
  const [isAgree, setIsAgree] = useState(false);

  const { account } = useAppSelector(({ wallet }) => wallet);
  const isSmDownScreen = useMedia(getBreakpointQuery(breakpoints.down('sm')));

  const handleIsAgree = () => {
    setIsAgree(!isAgree);
  };

  const handleClickConfirm = () => {
    const form = document.createElement('form');
    form.setAttribute('id', 'simplex-form');
    form.setAttribute('target', 'simplex-form');
    form.setAttribute('method', `${confirmData.method}`);
    form.setAttribute('action', `${confirmData.action}`);

    if (confirmData) {
      const objList = [
        {
          name: 'version',
          value: `${confirmData.version}`,
        },
        {
          name: 'partner',
          value: `${confirmData.partner}`,
        },
        {
          name: 'return_url_success',
          value: `${confirmData.return_url_success}`,
        },
        {
          name: 'return_url_fail',
          value: `${confirmData.return_url_fail}`,
        },
        {
          name: 'payment_id',
          value: `${confirmData?.payment_id}`,
        },
      ];

      objList.forEach((item) => {
        const obj = document.createElement('input');
        obj.setAttribute('type', 'hidden');
        obj.setAttribute('name', item.name);
        obj.setAttribute('value', item.value);
        form.appendChild(obj);
      });

      document.body.appendChild(form);
      window.open('about:blank', 'simplex-form');
      form.submit();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      width={{ xl: '498px', lg: '498px', md: '498px' }}
      handleOpen={handleOpen}
      shouldCloseOnOverlayClick
    >
      <ModalContents>
        <TitleBlock>
          <Title>Buy with simplex</Title>
        </TitleBlock>
        <SubTitleBlock>
          <SubTitle>Confirm your order</SubTitle>
        </SubTitleBlock>
        <ContentsBlock>
          <ContentsTop>
            <ContentsRow>
              <ContentsName>Estimated amount</ContentsName>
              <ContentsValue>
                {digitalAmount} {digitalCurrency}
              </ContentsValue>
            </ContentsRow>
            <ContentsRow>
              <ContentsName>Total</ContentsName>
              <ContentsValue>
                {fiatAmount} {fiatCurrency}
              </ContentsValue>
            </ContentsRow>
          </ContentsTop>
          <ContentsBottom>
            <p>Deposit to address of exchange account</p>
            <p>
              {isSmDownScreen ? convertAddress(account || '') : account || ''}
            </p>
          </ContentsBottom>
        </ContentsBlock>
        <DisclaimerBlock>
          <DisclaimerTitleBox>
            <span>Disclaimer</span>
          </DisclaimerTitleBox>
          <DisclaimerContents>
            You will now leave PlayDapp Market and jump to{' '}
            <span>Simplex.com</span>. Please contact{' '}
            <span>support@simplex.com</span> for any questions. PlayDapp does
            not assume any responsibility for any loss or damage caused by the
            use of the credit card payment service.
          </DisclaimerContents>
        </DisclaimerBlock>
        <CheckBoxBlock>
          <Form>
            <Form.Check
              id="agree"
              name="agree"
              checked={isAgree}
              onChange={handleIsAgree}
            >
              I have read and agreed the disclamer
            </Form.Check>
          </Form>
        </CheckBoxBlock>
        <ButtonBlock>
          <Button
            variant="outline"
            color="black"
            onClick={() => handleOpen(false)}
          >
            Cancel
          </Button>
          <Button disabled={!isAgree} onClick={handleClickConfirm}>
            Pay now
          </Button>
        </ButtonBlock>
      </ModalContents>
    </Modal>
  );
};

export default SimplexConfirmModal;
